/**
 * Saved-views strip for the Timers (Life Events) page.
 *
 * Views are named, rule-based filter sets — selecting one applies its whole
 * filter set + sort + view mode at once; they are never per-timer assignment.
 * This component is presentation-only: the page owns all state and
 * persistence (life_events_display namespace) and passes handlers down, so
 * the strip stays trivially testable.
 */
import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';

import { SavedTimerView } from '../contexts/PreferencesContext';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

interface TimerViewsBarProps {
  views: SavedTimerView[];
  activeViewId: string | null;
  defaultViewId: string | null;
  /** True when the on-screen filters have drifted from the active view. */
  modified: boolean;
  onSelect: (view: SavedTimerView) => void;
  onShowAll: () => void;
  onSaveChanges: () => void;
  onRevert: () => void;
  onSaveAs: (name: string, makeDefault: boolean) => void;
  onDelete: (view: SavedTimerView) => void;
  onSetDefault: (view: SavedTimerView | null) => void;
}

const TimerViewsBar: React.FC<TimerViewsBarProps> = ({
  views,
  activeViewId,
  defaultViewId,
  modified,
  onSelect,
  onShowAll,
  onSaveChanges,
  onRevert,
  onSaveAs,
  onDelete,
  onSetDefault,
}) => {
  const { dialogContentPaddingSx } = useLayoutTokens();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuViewId, setMenuViewId] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  const menuView = views.find((view) => view.id === menuViewId) ?? null;

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuViewId(null);
  };

  const openSaveDialog = () => {
    setSaveName('');
    setSaveAsDefault(false);
    setSaveDialogOpen(true);
  };

  const handleCreateView = () => {
    const name = saveName.trim();
    if (!name) return;
    onSaveAs(name, saveAsDefault);
    setSaveDialogOpen(false);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 1.5 }}
    >
      <Chip
        label="All timers"
        size="small"
        color={activeViewId === null ? 'primary' : 'default'}
        variant={activeViewId === null ? 'filled' : 'outlined'}
        onClick={onShowAll}
      />
      {views.map((view) => {
        const isActive = view.id === activeViewId;
        return (
          <Chip
            key={view.id}
            label={isActive && modified ? `${view.name} •` : view.name}
            size="small"
            icon={view.id === defaultViewId ? <StarIcon fontSize="small" /> : undefined}
            color={isActive ? 'primary' : 'default'}
            variant={isActive ? 'filled' : 'outlined'}
            onClick={() => onSelect(view)}
            onDelete={(event: React.MouseEvent<HTMLElement>) => {
              setMenuAnchor(event.currentTarget);
              setMenuViewId(view.id);
            }}
            deleteIcon={
              <MoreVertIcon aria-label={`Options for view ${view.name}`} />
            }
          />
        );
      })}
      {activeViewId !== null && modified && (
        <>
          <Button size="small" variant="contained" onClick={onSaveChanges}>
            Save changes
          </Button>
          <Button size="small" onClick={onRevert}>
            Revert
          </Button>
        </>
      )}
      <Button size="small" startIcon={<AddIcon />} onClick={openSaveDialog}>
        Save view…
      </Button>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuView && menuView.id === defaultViewId ? (
          <MenuItem
            onClick={() => {
              onSetDefault(null);
              closeMenu();
            }}
          >
            Clear default
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              if (menuView) onSetDefault(menuView);
              closeMenu();
            }}
          >
            Set as default
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menuView) onDelete(menuView);
            closeMenu();
          }}
        >
          Delete view
        </MenuItem>
      </Menu>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save current filters as a view</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <TextField
            autoFocus
            margin="dense"
            label="View name"
            fullWidth
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={saveAsDefault}
                onChange={(e) => setSaveAsDefault(e.target.checked)}
              />
            }
            label="Apply this view when the page loads"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateView}
            disabled={!saveName.trim()}
          >
            Create view
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default TimerViewsBar;
